import { NgClass } from '@angular/common';
import { Component, EventEmitter, OnDestroy, Output } from '@angular/core';
import { IconComponent } from 'components/layout/icon/icon.component';
import { ViewTaskflowComponent } from 'components/task/taskflow/view-taskflow.component';
import { TaskActionType } from 'enums/TaskActionType';
import { TaskActionModel } from 'models/generated/Entities.GormCrm.TaskAction';
import { CustomDatePipe } from 'pipes/custom-date.pipe';
import { TranslatePipe } from 'pipes/translate.pipe';
import { Subscription } from 'rxjs';
import { NavigationService } from 'services/navigation.service';
import { PushService } from 'services/push.service';
import { TaskService } from 'services/task.service';
import { UserService } from 'services/user.service';

@Component({
	selector: 'agendas',
	templateUrl: './agendas.component.html',
	styleUrls: ['./agendas.component.scss'],
	imports: [IconComponent, NgClass, TranslatePipe, CustomDatePipe]
})
export class AgendasComponent implements OnDestroy {

	@Output() onClose = new EventEmitter();
	@Output() overdueAgendasChanged = new EventEmitter<number>();

	onTaskflowChanges: Subscription;
	agendas: TaskActionModel[] = [];
	overdueAgendasCount = 0;
	agendaTimer?: any;

	private currentUserId: string;

	constructor(private navigation: NavigationService,
		private translate: TranslatePipe,
		private taskService: TaskService,
		private userService: UserService,
		private readonly pushService: PushService,
		private readonly customDate: CustomDatePipe) {

		this.currentUserId = this.userService.getCurrentUser().id!;
		(new Promise(f => setTimeout(f, 2000))).then(_ => this.updateAgendas()); // Delay initial update so it doesn't block more essential API requests

		this.onTaskflowChanges = this.pushService.onTaskflowChanges.subscribe(event => {
			if (this.agendas.filter(action => action.id === event.data.taskActionId || this.currentUserId === event.data.userId).length > 0) {
				this.updateAgendas();
			}
		});
	}

	updateAgendas() {
		this.taskService.getUsersOpenTaskActions(this.currentUserId).then((actions) => {
			this.agendas = actions
				.filter((action) => { return action.plannedDate && !action.dateOnly; })
				.sort((a, b) => new Date(a.plannedDate!).getTime() - new Date(b.plannedDate!).getTime());
			this.updateOverdueAgendasCount();
		});
	}

	updateOverdueAgendasCount() {
		this.overdueAgendasCount = this.agendas.filter((agenda) => new Date(agenda.plannedDate!).getTime() < new Date().getTime()).length;
		this.overdueAgendasChanged.emit(this.overdueAgendasCount);

		this.agendaTimer = undefined;
		var futureAgendas = this.agendas.filter((agenda) => new Date(agenda.plannedDate!).getTime() > new Date().getTime());
		if (futureAgendas.length === 0) return;

		var millisecondsTillNextAgenda = new Date(futureAgendas[0].plannedDate!).getTime() - new Date().getTime();

		this.agendaTimer = setTimeout(() => {
			this.updateOverdueAgendasCount();
		}, millisecondsTillNextAgenda + 10);
	}

	getType(taskAction: TaskActionModel): string {
		return taskAction.taskflow!.title + ' - ' + this.translate.transform('enum.task-action-type.' + TaskActionType[taskAction.type]);
	}

	getDateRepresentation(taskAction: TaskActionModel): string {
		var differenceInDays = +this.customDate.transform(taskAction.plannedDate!, 'daysDiffer');
		if (differenceInDays === -1) return this.translate.transform('general.yesterday');
		if (differenceInDays === 0) return this.translate.transform('general.today');
		if (differenceInDays === 1) return this.translate.transform('general.tomorrow');

		return this.customDate.transform(taskAction.plannedDate, 'fullDate');
	}

	isPlannedDateOverdue(agenda: TaskActionModel): boolean {
		return new Date().getTime() > new Date(agenda.plannedDate!).getTime();
	}

	agendaClicked(action: TaskActionModel) {
		this.onClose.emit();
		this.navigation.refreshView(ViewTaskflowComponent).then(view => {
			view.selectedActionId = action.id;
			view.taskflowId = action.taskflowId!;
		});
	}

	showDateHeader(index: number) {
		return index == 0 ||
			this.customDate.transform(this.agendas[index - 1].plannedDate, 'shortDate') != this.customDate.transform(this.agendas[index].plannedDate, 'shortDate');
	}

	ngOnDestroy() {
		if (this.agendaTimer) clearTimeout(this.agendaTimer);
		this.onTaskflowChanges.unsubscribe();
	}
}
